import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../api';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Layout from '../components/Layout';

// const BASE_URL = 'http://localhost:5000';
const BASE_URL = 'http://192.168.0.57:5000';

const TITLES  = ['Mr.', 'Mrs.', 'Ms.', 'Dr.', 'Prof.'];

const EMPTY_FORM = {
  title: 'Mr.', first_name: '', middle_name: '', last_name: '',
  email: '', alt_email: '', mobile: '', alt_mobile: '',
  address: '', voter_no: '', pan_no: '', aadhar_no: '',
};

export default function CabOwnerDetails() {
  const navigate = useNavigate();

  // ── Table ──
  const [data,       setData]       = useState([]);
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total,      setTotal]      = useState(0);
  const [search,     setSearch]     = useState('');
  const [loading,    setLoading]    = useState(false);

  // ── Modal ──
  const [modalOpen,  setModalOpen]  = useState(false);
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [editId,     setEditId]     = useState(null);
  const [saving,     setSaving]     = useState(false);

  // ── Files ──
  const [files,      setFiles]      = useState({ voter_file: null, pan_file: null, aadhar_file: null });
  const [previews,   setPreviews]   = useState({ voter_file: '', pan_file: '', aadhar_file: '' });
  const voterRef  = useRef();
  const panRef    = useRef();
  const aadharRef = useRef();

  // ── View modal ──
  const [viewOwner, setViewOwner] = useState(null);

  // ── Auth ──
  useEffect(() => {
    if (!localStorage.getItem('token')) navigate('/login');
  }, []);

  // ── Fetch ──
  const fetchData = useCallback((pg = page, q = search) => {
    setLoading(true);
    API.get('/cab-owners', { params: { page: pg, limit: 10, search: q } })
      .then(r => {
        setData(r.data.data);
        setTotalPages(r.data.totalPages);
        setTotal(r.data.total);
      })
      .catch(() => toast.error('Failed to load owners'))
      .finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => { fetchData(page, search); }, [page]);

  useEffect(() => {
    const t = setTimeout(() => { setPage(1); fetchData(1, search); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // ── Open Add ──
  const openAdd = () => {
    setForm(EMPTY_FORM);
    setEditId(null);
    setFiles({ voter_file: null, pan_file: null, aadhar_file: null });
    setPreviews({ voter_file: '', pan_file: '', aadhar_file: '' });
    setModalOpen(true);
  };

  // ── Open Edit ──
  const openEdit = (row) => {
    setForm({
      title:       row.title       || 'Mr.',
      first_name:  row.first_name  || '',
      middle_name: row.middle_name || '',
      last_name:   row.last_name   || '',
      email:       row.email       || '',
      alt_email:   row.alt_email   || '',
      mobile:      row.mobile      || '',
      alt_mobile:  row.alt_mobile  || '',
      address:     row.address     || '',
      voter_no:    row.voter_no    || '',
      pan_no:      row.pan_no      || '',
      aadhar_no:   row.aadhar_no   || '',
    });
    setEditId(row.owner_id);
    setFiles({ voter_file: null, pan_file: null, aadhar_file: null });
    setPreviews({
      voter_file:  row.voter_file  ? `${BASE_URL}/${row.voter_file}`  : '',
      pan_file:    row.pan_file    ? `${BASE_URL}/${row.pan_file}`    : '',
      aadhar_file: row.aadhar_file ? `${BASE_URL}/${row.aadhar_file}` : '',
    });
    setModalOpen(true);
  };

  // ── Form change ──
  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  // ── File change ──
  const handleFile = (e, field) => {
    const file = e.target.files[0];
    if (!file) return;
    setFiles(f => ({ ...f, [field]: file }));
    setPreviews(p => ({ ...p, [field]: URL.createObjectURL(file) }));
  };

  // ── Submit ──
  const handleSubmit = async () => {
    if (!form.first_name.trim()) { toast.warning('First name is required'); return; }
    if (!form.last_name.trim())  { toast.warning('Last name is required');  return; }
    if (!form.email.trim())      { toast.warning('Email is required');      return; }
    if (!form.mobile.trim())     { toast.warning('Mobile is required');     return; }

    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (files.voter_file)  fd.append('voter_file',  files.voter_file);
      if (files.pan_file)    fd.append('pan_file',    files.pan_file);
      if (files.aadhar_file) fd.append('aadhar_file', files.aadhar_file);

      if (editId) {
        await API.put(`/cab-owners/${editId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Cab owner updated successfully');
      } else {
        await API.post('/cab-owners', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Cab owner added successfully');
      }
      setModalOpen(false);
      fetchData(page, search);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Operation failed');
    }
    setSaving(false);
  };

  // ── Delete ──
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this cab owner? This cannot be undone.')) return;
    try {
      await API.delete(`/cab-owners/${id}`);
      toast.success('Deleted successfully');
      fetchData(page, search);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    }
  };

  // ── Doc link ──
  const DocLink = ({ path, label }) => {
    if (!path) return <span style={{ color: '#ccc', fontSize: 11 }}>—</span>;
    const isPdf = path.toLowerCase().endsWith('.pdf');
    return (
      <a href={`${BASE_URL}/${path}`} target="_blank" rel="noreferrer"
        style={S.docLink}>
        {isPdf ? '📄' : '🖼️'} {label}
      </a>
    );
  };

  // ── File upload field ──
  const FileField = ({ label, field, inputRef, existingPath }) => {
    const isPdf  = existingPath?.toLowerCase().endsWith('.pdf');
    const isNew  = !!files[field];
    const preview = previews[field];
    return (
      <div style={S.fg}>
        <label style={S.label}>{label}</label>
        <div style={S.fileBox}>
          <button type="button" style={S.fileBtn}
            onClick={() => inputRef.current.click()}>
            📎 {isNew ? 'Change File' : existingPath ? 'Replace File' : 'Upload File'}
          </button>
          <input ref={inputRef} type="file" accept=".jpg,.jpeg,.png,.pdf"
            style={{ display: 'none' }}
            onChange={e => handleFile(e, field)} />
          {preview && !isPdf && (
            <img src={preview} alt={label}
              style={{ width: 60, height: 40, objectFit: 'cover', borderRadius: 6, marginLeft: 8 }} />
          )}
          {preview && isPdf && (
            <a href={preview} target="_blank" rel="noreferrer" style={{ marginLeft: 8, fontSize: 12, color: '#6C63FF' }}>
              View PDF
            </a>
          )}
          {isNew && (
            <span style={{ fontSize: 11, color: '#1a9e5c', marginLeft: 8 }}>✓ New file selected</span>
          )}
        </div>
        <span style={{ fontSize: 11, color: '#aaa' }}>JPG / PNG / PDF, max 2MB</span>
      </div>
    );
  };

  return (
    <Layout activePage="Cab Owner Details">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header */}
      <div style={S.header}>
        <div>
          <h2 style={S.title}>Cab Owner Details</h2>
          <p style={S.subtitle}>Manage cab owner records and their documents</p>
        </div>
        <button style={S.addBtn} onClick={openAdd}>➕ Add Cab Owner</button>
      </div>

      {/* Search */}
      <div style={S.searchWrap}>
        <span style={{ fontSize: 14, color: '#aaa' }}>🔍</span>
        <input
          type="text" placeholder="Search by name, mobile or email..."
          value={search} onChange={e => setSearch(e.target.value)}
          style={S.searchInput}
        />
        {search && <button style={S.clearBtn} onClick={() => setSearch('')}>✕</button>}
      </div>

      {/* Table Card */}
      <div style={S.card}>
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr style={S.theadRow}>
                <th style={S.th}>#</th>
                <th style={S.th}>Name</th>
                <th style={S.th}>Mobile</th>
                <th style={S.th}>Email</th>
                <th style={S.th}>Address</th>
                <th style={S.th}>Documents</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={S.emptyCell}>Loading…</td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan={7} style={S.emptyCell}>No records found</td></tr>
              ) : data.map((row, i) => (
                <tr key={row.owner_id} style={S.tr}>
                  <td style={S.td}>
                    <span style={S.serial}>{(page - 1) * 10 + i + 1}</span>
                  </td>
                  <td style={S.td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={S.avatar}>
                        {row.first_name?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13, color: '#1a1d2e' }}>
                          {row.title} {row.first_name} {row.middle_name} {row.last_name}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={S.td}>
                    <span style={S.mobileChip}>{row.mobile}</span>
                    {/* {row.alt_mobile && (
                      <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>{row.alt_mobile}</div>
                    )} */}
                  </td>
                  <td style={{ ...S.td, fontSize: 12, color: '#555' }}>{row.email}</td>
                  <td style={{ ...S.td, fontSize: 12, color: '#777', maxWidth: 160 }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.address || '—'}
                    </div>
                  </td>
                  <td style={S.td}>
                    <div style={{ display: 'flex',  gap: 4 }}>
                      <DocLink path={row.voter_file}  label="Voter" />
                      <DocLink path={row.pan_file}    label="PAN"   />
                      <DocLink path={row.aadhar_file} label="Aadhar"/>
                    </div>
                  </td>
                  <td style={{ ...S.td, textAlign: 'right' }}>
                    <button style={S.viewBtn} onClick={() => setViewOwner(row)}>View</button>
                    <button style={S.editBtn} onClick={() => openEdit(row)}>Edit</button>
                    <button style={S.delBtn}  onClick={() => handleDelete(row.owner_id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={S.pagination}>
          <span style={S.paginInfo}>
            {total > 0 ? `Showing ${(page - 1) * 10 + 1}–${Math.min(page * 10, total)} of ${total}` : ''}
          </span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button style={S.pgBtn} disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
            <span style={S.paginInfo}>Page {page} / {totalPages || 1}</span>
            <button style={S.pgBtn} disabled={page === totalPages || totalPages === 0}
              onClick={() => setPage(p => p + 1)}>Next →</button>
          </div>
        </div>
      </div>

      {/* ── Add / Edit Modal ── */}
      {modalOpen && (
        <div style={S.overlay} onClick={() => !saving && setModalOpen(false)}>
          <div style={S.modalBox} onClick={e => e.stopPropagation()}>
            <div style={S.modalHead}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
                {editId ? '✏️ Edit Cab Owner' : '➕ Add Cab Owner'}
              </h3>
              <button style={S.closeBtn} onClick={() => !saving && setModalOpen(false)}>✕</button>
            </div>

            <div style={S.modalBody}>

              {/* Personal Info */}
              <div style={S.section}>Personal Information</div>
              <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div style={S.fg}>
                  <label style={S.label}>Title</label>
                  <select name="title" value={form.title} onChange={handleChange} style={S.input}>
                    {TITLES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div style={S.fg}>
                  <label style={S.label}>First Name <span style={S.req}>*</span></label>
                  <input name="first_name" value={form.first_name} onChange={handleChange}
                    placeholder="First name" style={S.input} />
                </div>
                <div style={S.fg}>
                  <label style={S.label}>Middle Name</label>
                  <input name="middle_name" value={form.middle_name} onChange={handleChange}
                    placeholder="Middle name" style={S.input} />
                </div>
                <div style={S.fg}>
                  <label style={S.label}>Last Name <span style={S.req}>*</span></label>
                  <input name="last_name" value={form.last_name} onChange={handleChange}
                    placeholder="Last name" style={S.input} />
                </div>
              </div>

              {/* Contact */}
              <div style={S.section}>Contact Details</div>
              <div style={S.grid2}>
                <div style={S.fg}>
                  <label style={S.label}>Email <span style={S.req}>*</span></label>
                  <input name="email" type="email" value={form.email} onChange={handleChange}
                    placeholder="primary@email.com" style={S.input} />
                </div>
                <div style={S.fg}>
                  <label style={S.label}>Alternate Email</label>
                  <input name="alt_email" type="email" value={form.alt_email} onChange={handleChange}
                    placeholder="alternate@email.com" style={S.input} />
                </div>
                <div style={S.fg}>
                  <label style={S.label}>Mobile <span style={S.req}>*</span></label>
                  <input name="mobile" value={form.mobile} onChange={handleChange}
                    placeholder="Primary mobile" style={S.input} />
                </div>
                <div style={S.fg}>
                  <label style={S.label}>Alternate Mobile</label>
                  <input name="alt_mobile" value={form.alt_mobile} onChange={handleChange}
                    placeholder="Alternate mobile" style={S.input} />
                </div>
              </div>

              <div style={S.fg}>
                <label style={S.label}>Address</label>
                <textarea name="address" value={form.address} onChange={handleChange}
                  placeholder="Full address..." rows={2}
                  style={{ ...S.input, resize: 'vertical', fontFamily: 'inherit' }} />
              </div>

              {/* Documents */}
              <div style={S.section}>Identity Documents</div>
              <div style={S.grid2}>
                <div style={S.fg}>
                  <label style={S.label}>Voter ID Number</label>
                  <input name="voter_no" value={form.voter_no} onChange={handleChange}
                    placeholder="Voter ID number" style={S.input} />
                </div>
                <FileField label="Voter ID File" field="voter_file"
                  inputRef={voterRef}
                  existingPath={editId ? data.find(r => r.owner_id === editId)?.voter_file : ''} />

                <div style={S.fg}>
                  <label style={S.label}>PAN Number</label>
                  <input name="pan_no" value={form.pan_no} onChange={handleChange}
                    placeholder="PAN number" style={S.input} />
                </div>
                <FileField label="PAN File" field="pan_file"
                  inputRef={panRef}
                  existingPath={editId ? data.find(r => r.owner_id === editId)?.pan_file : ''} />

                <div style={S.fg}>
                  <label style={S.label}>Aadhar Number</label>
                  <input name="aadhar_no" value={form.aadhar_no} onChange={handleChange}
                    placeholder="Aadhar number" style={S.input} />
                </div>
                <FileField label="Aadhar File" field="aadhar_file"
                  inputRef={aadharRef}
                  existingPath={editId ? data.find(r => r.owner_id === editId)?.aadhar_file : ''} />
              </div>

            </div>

            <div style={S.modalFoot}>
              <button style={S.cancelBtn} onClick={() => !saving && setModalOpen(false)}>Cancel</button>
              <button style={{ ...S.submitBtn, opacity: saving ? 0.7 : 1 }}
                onClick={handleSubmit} disabled={saving}>
                {saving ? 'Saving…' : editId ? 'Update Owner' : 'Add Owner'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── View Modal ── */}
      {viewOwner && (
        <div style={S.overlay} onClick={() => setViewOwner(null)}>
          <div style={{ ...S.modalBox, maxWidth: 520 }} onClick={e => e.stopPropagation()}>
            <div style={S.modalHead}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
                👤 {viewOwner.title} {viewOwner.first_name} {viewOwner.last_name}
              </h3>
              <button style={S.closeBtn} onClick={() => setViewOwner(null)}>✕</button>
            </div>
            <div style={S.modalBody}>
              {/* Avatar */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                <div style={{ ...S.avatar, width: 56, height: 56, fontSize: 22 }}>
                  {viewOwner.first_name?.[0]?.toUpperCase() || '?'}
                </div>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#1a1d2e' }}>
                    {viewOwner.title} {viewOwner.first_name} {viewOwner.middle_name} {viewOwner.last_name}
                  </div>
                  <div style={{ fontSize: 13, color: '#999' }}>{viewOwner.email}</div>
                </div>
              </div>

              <div style={S.grid2}>
                {[
                  ['Mobile',          viewOwner.mobile],
                  ['Alt Mobile',      viewOwner.alt_mobile],
                  ['Alt Email',       viewOwner.alt_email],
                  ['Address',         viewOwner.address],
                  ['Voter No',        viewOwner.voter_no],
                  ['PAN No',          viewOwner.pan_no],
                  ['Aadhar No',       viewOwner.aadhar_no],
                ].map(([k, v]) => v ? (
                  <div key={k}>
                    <div style={{ fontSize: 11, color: '#aaa', fontWeight: 600, textTransform: 'uppercase' }}>{k}</div>
                    <div style={{ fontSize: 13, color: '#333', fontWeight: 500, marginTop: 2 }}>{v}</div>
                  </div>
                ) : null)}
              </div>

              {/* Document links */}
              <div style={{ marginTop: 16 }}>
                <div style={S.section}>Documents</div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {[
                    { path: viewOwner.voter_file,  label: 'Voter ID' },
                    { path: viewOwner.pan_file,    label: 'PAN Card' },
                    { path: viewOwner.aadhar_file, label: 'Aadhar'   },
                  ].map(d => d.path ? (
                    <a key={d.label} href={`${BASE_URL}/${d.path}`} target="_blank" rel="noreferrer"
                      style={S.docBadge}>
                      📄 {d.label}
                    </a>
                  ) : null)}
                  {!viewOwner.voter_file && !viewOwner.pan_file && !viewOwner.aadhar_file && (
                    <span style={{ color: '#bbb', fontSize: 13 }}>No documents uploaded</span>
                  )}
                </div>
              </div>
            </div>
            <div style={S.modalFoot}>
              <button style={S.submitBtn} onClick={() => setViewOwner(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

/* ─── Styles ─── */
const S = {
  header:   { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  title:    { margin: 0, fontSize: 26, fontWeight: 800, color: '#1a1d2e', letterSpacing: '-0.5px' },
  subtitle: { margin: '4px 0 0', fontSize: 13, color: '#999' },
  addBtn:   {
    padding: '10px 22px', background: 'linear-gradient(135deg,#6C63FF,#43BCCD)',
    color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer',
    fontSize: 14, fontWeight: 700, fontFamily: 'inherit',
    boxShadow: '0 4px 14px rgba(108,99,255,0.35)'
  },

  searchWrap: {
    display: 'flex', alignItems: 'center', gap: 8, background: '#fff',
    border: '1px solid #e8e8e8', borderRadius: 10, padding: '9px 16px',
    marginBottom: 20, maxWidth: 440, boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
  },
  searchInput: { border: 'none', outline: 'none', fontSize: 13, flex: 1, fontFamily: 'inherit', color: '#333' },
  clearBtn:    { background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: 12, padding: 0 },

  card:      { background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px rgba(0,0,0,0.06)', overflow: 'hidden' },
  table:     { width: '100%', borderCollapse: 'collapse' },
  theadRow:  { background: '#fafafa' },
  th: {
    padding: '12px 16px', fontSize: 11, fontWeight: 700, color: '#999',
    textAlign: 'left', borderBottom: '2px solid #f0f0f0',
    textTransform: 'uppercase', letterSpacing: '0.5px'
  },
  tr:        { transition: 'background 0.12s' },
  td:        { padding: '13px 16px', fontSize: 13, color: '#333', borderBottom: '1px solid #f8f8f8' },
  emptyCell: { textAlign: 'center', padding: 40, color: '#bbb', fontSize: 14 },

  serial:    { background: '#f3f2ff', color: '#6C63FF', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 700 },
  avatar:    {
    width: 36, height: 36, background: 'linear-gradient(135deg,#6C63FF,#43BCCD)',
    borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#fff', fontWeight: 800, fontSize: 14, flexShrink: 0
  },
  mobileChip: { background: '#f0f8ff', color: '#1a6eb5', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 600 },

  docLink: {
    fontSize: 12, color: '#6C63FF', fontWeight: 600,
    textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4
  },
  docBadge: {
    padding: '6px 14px', background: '#f3f2ff', color: '#6C63FF',
    borderRadius: 20, fontSize: 13, fontWeight: 600,
    textDecoration: 'none', border: '1px solid #d4d0ff'
  },

  viewBtn: {
    marginRight: 5, background: '#f0fff6', color: '#1a9e5c',
    border: '1px solid #b8f0d0', padding: '4px 12px',
    borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit'
  },
  editBtn: {
    marginRight: 5, background: '#f3f2ff', color: '#6C63FF',
    border: '1px solid #d4d0ff', padding: '4px 12px',
    borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit'
  },
  delBtn: {
    background: '#fff2f5', color: '#FF6584', border: '1px solid #ffd0d8',
    padding: '4px 12px', borderRadius: 20, cursor: 'pointer',
    fontSize: 12, fontWeight: 700, fontFamily: 'inherit'
  },

  pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderTop: '1px solid #f0f0f0' },
  paginInfo:  { fontSize: 12, color: '#999' },
  pgBtn: {
    padding: '5px 14px', background: '#f3f2ff', color: '#6C63FF',
    border: '1px solid #d4d0ff', borderRadius: 8, cursor: 'pointer',
    fontSize: 12, fontWeight: 600, fontFamily: 'inherit'
  },

  overlay:  { position: 'fixed', inset: 0, background: 'rgba(10,10,30,0.55)', zIndex: 999,
    display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(2px)' },
  modalBox: { background: '#fff', borderRadius: 20, width: 720, maxWidth: '96vw', maxHeight: '90vh',
    display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.2)' },
  modalHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '18px 22px', borderBottom: '1px solid #f0f0f0', flexShrink: 0 },
  modalBody: { padding: '20px 22px', overflowY: 'auto', flex: 1 },
  modalFoot: { display: 'flex', justifyContent: 'flex-end', gap: 10,
    padding: '14px 22px', borderTop: '1px solid #f0f0f0', flexShrink: 0 },
  closeBtn:  { background: '#ff6b6b22', border: 'none', color: '#ff6b6b',
    borderRadius: '50%', width: 30, height: 30, cursor: 'pointer', fontWeight: 800, fontSize: 14 },
  cancelBtn: { padding: '9px 20px', background: '#f5f5f5', color: '#666',
    border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  submitBtn: { padding: '9px 22px', background: 'linear-gradient(135deg,#6C63FF,#43BCCD)',
    color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer',
    fontSize: 13, fontWeight: 700, fontFamily: 'inherit' },

  section: { fontSize: 11, fontWeight: 800, color: '#6C63FF', textTransform: 'uppercase',
    letterSpacing: '0.8px', margin: '16px 0 10px', paddingBottom: 6,
    borderBottom: '1.5px solid #f3f2ff' },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 },
  fg:    { display: 'flex', flexDirection: 'column', gap: 5 },
  label: { fontSize: 12, fontWeight: 600, color: '#555' },
  req:   { color: '#ff6b6b' },
  input: { padding: '9px 12px', border: '1.5px solid #e0e0e0', borderRadius: 10,
    fontSize: 13, fontFamily: 'inherit', color: '#333', outline: 'none',
    width: '100%', boxSizing: 'border-box' },

  fileBox: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 },
  fileBtn: { padding: '7px 14px', background: '#f3f2ff', color: '#6C63FF',
    border: '1px solid #d4d0ff', borderRadius: 8, cursor: 'pointer',
    fontSize: 12, fontWeight: 600, fontFamily: 'inherit' },
};
